import { useState, useEffect, useCallback } from 'react';

interface UseScrollableContentReturn {
  isScrollable: boolean;
  canDrag: boolean;
  canScroll: boolean;
  handleContentSizeChange: (contentWidth: number, contentHeight: number) => void;
  handleLayout: (event: any) => void;
}

/**
 * Hook to manage scrollable content logic for modal screens
 * Determines whether content should be scrollable or draggable based on content overflow
 */
export const useScrollableContent = (): UseScrollableContentReturn => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const handleContentSizeChange = useCallback((contentWidth: number, contentHeight: number) => {
    setContentHeight(contentHeight);
  }, []);

  const handleLayout = useCallback((event: any) => {
    const height = event.nativeEvent.layout.height;
    setContainerHeight(height);
  }, []);

  // Determine if content is scrollable only when we have both measurements
  useEffect(() => {
    if (containerHeight > 0 && contentHeight > 0) {
      const scrollable = contentHeight > containerHeight;
      setIsScrollable(scrollable);
    }
  }, [containerHeight, contentHeight]);

  // Only allow drag-to-dismiss when content is not scrollable
  // Only allow scrolling when content is scrollable
  const canDrag = !isScrollable;
  const canScroll = isScrollable;

  return {
    isScrollable,
    canDrag,
    canScroll,
    handleContentSizeChange,
    handleLayout,
  };
};
